import { useEventEmitter } from "ahooks";
import dayjs from "dayjs";

import { useNavigate } from "react-router-dom";

import CalendarMonth from "@mui/icons-material/CalendarMonth";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { Chip, IconButton, Rating, Tooltip, Typography } from "@mui/material";

import { ApiHotelItem } from "@/api/hotel";
import { HotelColors } from "@/constants/config";
import { useListenEvent } from "@/hooks/useEventEmitter";
import useHotelSearchCondition from "@/hooks/useHotelSearchCondition";
import usePlatform from "@/hooks/usePlatform";

import useHotelPrice from "../../hooks/usePrice";
import SummaryOptions from "../SummaryOptions";

function getBgColorByValue(value: string) {
  const v = +value.replace("%", "");
  if (v > 80) {
    return HotelColors.C80;
  } else if (v > 50) {
    return HotelColors.C50;
  } else if (v > 20) {
    return HotelColors.C20;
  } else {
    return HotelColors.C00;
  }
}

function formatDate(date) {
  return dayjs(date).format("MM/DD/YYYY");
}

const HotelItem = (props: {
  usedDetails?: boolean;
  data: ApiHotelItem;
  onLoad: () => void;
  onAlert: (data: ApiHotelItem) => void;
}) => {
  const { data, usedDetails = false } = props;

  const bgColor = getBgColorByValue(data.availability);
  const { condition } = useHotelSearchCondition();
  const navigator = useNavigate();

  const { isMobile } = usePlatform();
  const { pointValue, pointPrice, isSearchData } = useHotelPrice(data);

  let hotel_link: string | undefined;
  const hotel_search = condition.startDate && condition.endDate;
  if (hotel_search) {
    const checkinDate = condition.startDate
      ? dayjs(condition.startDate).format("YYYY-MM-DD")
      : "---";
    let checkoutDate = condition.endDate ? dayjs(condition.endDate).format("YYYY-MM-DD") : "----"; // format YYYY-MM-DD
    // If checkin date is the same as checkout date, add 1 day to checkout date
    if (checkinDate === checkoutDate) {
      checkoutDate = dayjs(checkinDate).add(1, "day").format("YYYY-MM-DD");
    }

    const id = data.id.split("_")[1];
    const HOTEL_LINK: { [key: string]: string } = {
      hyatt: `https://www.hyatt.com/shop/rooms/${id}?checkinDate=${checkinDate}&checkoutDate=${checkoutDate}&rooms=1&adults=2&kids=0&rate=Standard&corp_id=&rateFilter=woh`,
      // ihg: `https://www.ihg.com/hotels/us/en/find-hotels/select-roomrate?qSlH=NYCLF&qAdlt=2&qRms=1&qCiD=${checkinDate.split('-')[2]}&qCoD=${checkoutDate.split('-')[2]}&qPt=POINTS&qCpid=&qCiMy=${checkinDate.split('-')[1]}${checkinDate.split('-')[0].slice(-2)}&qCoMy=${checkoutDate.split('-')[1]}${checkoutDate.split('-')[0].slice(-2)}&qDest=%20%20%20&qRtP=${id}`,
      ihg: `https://www.ihg.com/hotels/us/en/find-hotels/select-roomrate?qDest=United%20States&qPt=POINTS&qCiD=${dayjs(
        checkinDate
      ).format("D")}&qCoD=${dayjs(checkoutDate).format("D")}&qCiMy=${dayjs(checkinDate).format(
        "YYYYMM"
      )}&qCoMy=${dayjs(checkoutDate).format(
        "YYYYMM"
      )}&qAdlt=2&qChld=0&qRms=1&qRtP=IVANI&qAAR=6CBARC&qSlH=MLEMM&qAkamaiCC=US&srb_u=1&qExpndSrch=false&qSrt=sAV&qBrs=6c.hi.ex.sb.ul.ic.cp.cw.in.vn.cv.rs.ki.ma.sp.va.sp.re.vx.nd.ii.sx.we.lx.sn&qWch=0&qSmP=0&qRad=30&qRdU=mi&setPMCookies=false&qpMbw=0&qpMn=0&qLoSe=false&qRmFltr=&qChAge=`,
      // ihg: `https://www.ihg.com/hotels/us/en/find-hotels`,
      marriott: `https://www.marriott.com/reservation/availabilitySearch.mi?fromDate=${encodeURIComponent(
        formatDate(checkinDate)
      )}&toDate=${encodeURIComponent(
        formatDate(checkoutDate)
      )}&numberOfRooms=1&numberOfAdults=2&guestCountBox=2+Adults+Per+Room&childrenCountBox=0+Children+Per+Room&roomCountBox=1+Rooms&childrenCount=0&clusterCode=none&isHwsGroupSearch=true&propertyCode=${id}&useRewardsPoints=true&flexibleDateSearch=false&showFullPrice=true&corporateCode=`,
      hilton: `https://www.hilton.com/en/book/reservation/rooms/?ctyhocn=${id}&arrivalDate=${checkinDate}&departureDate=${checkoutDate}&redeemPts=true&room1NumAdults=2`,
    };
    hotel_link = HOTEL_LINK[data.brand];
    // console.log("hotel_link: ", HOTEL_LINK["ihg"], dayjs(checkinDate).format("D"))
  }

  return (
    <div
      className="p-[15px] min-[700px]:flex min-[700px]:p-[20px]"
      onClick={() => {
        if (hotel_link) {
          window.open(hotel_link, "_blank");
          return;
        }
        if (props.usedDetails) {
          return;
        }
        if (isMobile) {
          navigator(`/hotel/details?id=${encodeURIComponent(data.id)}`);
        } else {
          window.open(`/hotel/details?id=${encodeURIComponent(data.id)}`, "_blank");
        }
      }}
    >
      <img
        className="w-[100%] h-auto min-[700px]:w-[200px] min-[700px]:h-[133px]"
        onLoad={props.onLoad}
        src={data.image}
        alt=""
      />

      <div className="flex-1 flex items-center justify-between">
        <div className="h-[100%] flex flex-col justify-center min-[700px]:ml-[20px]">
          <Typography fontSize={18} sx={{ fontWeight: "bold" }}>
            {data.name}
          </Typography>

          <div className="mt-[10px] flex items-center">
            <div
              className="w-[170px] flex items-center space-x-[4px] cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (isMobile) {
                  window.open(data.g_link, "_parent");
                } else {
                  window.open(data.g_link, "_target");
                }
              }}
            >
              <Typography fontSize={14}>{data.g_rating}</Typography>
              <Rating
                size="small"
                name="read-only"
                value={Number(data.g_rating)}
                readOnly
                precision={0.1}
              />
              <Typography fontSize={14}>
                ({data?.g_rating_total.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") || 0})
              </Typography>
            </div>
            {!hotel_search && (
              <Chip
                size="small"
                sx={{ backgroundColor: bgColor, color: "#fff", marginLeft: "10px" }}
                label={`${data.availability} Available`}
              />
            )}
          </div>

          {isMobile && usedDetails ? null : (
            <>
              <div className="mt-[10px] flex">
                <Typography fontSize={14} className="!mr-[3px]">
                  {isSearchData ? "Cash Price:" : "Point Value:"}
                </Typography>
                <Tooltip title={"min - median - max"} placement="top" arrow>
                  <Typography fontSize={14}>{pointValue}</Typography>
                </Tooltip>
              </div>

              <div className="mt-[10px] flex">
                <Typography fontSize={14} className="!mr-[4px]">
                  Point Price:{" "}
                </Typography>
                <Tooltip title={"min - median - max"} placement="top" arrow>
                  <Typography fontSize={14}>{pointPrice}</Typography>
                </Tooltip>
              </div>
            </>
          )}
        </div>
      </div>

      {isMobile && usedDetails ? null : (
        <div className="flex items-center justify-between max-[700px]:mt-[10px]">
          <SummaryOptions data={data} onLoad={props.onLoad} />
          {
            <div className="flex flex-col ml-[15px] mr-[5px]">
              {!usedDetails && (
                <IconButton onClick={(e) => {
                  e.stopPropagation();
                  if (isMobile) {
                    navigator(`/hotel/details?id=${encodeURIComponent(data.id)}`);
                  } else {
                    window.open(`/hotel/details?id=${encodeURIComponent(data.id)}`, "_blank");
                  }
                }} className="!ml-[6px] min-[700px]:ml-[0px]" size="small">
                  <CalendarMonth color="primary" />
                </IconButton>
              )}

              <IconButton
                className="!ml-[6px] min-[700px]:ml-[0px]"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  props.onAlert(props.data);
                }}
              >
                <NotificationsIcon />
              </IconButton>
            </div>
          }
        </div>
      )}
    </div>
  );
};

export default HotelItem;
